"use client";

import { useEffect } from "react";

import { ensureClarity } from "@/lib/analytics/clarity";

/**
 * Defers Microsoft Clarity bootstrap until first user interaction OR 10s timeout
 * (whichever comes first). Previously loaded via Next.js `<Script strategy="afterInteractive">`
 * which fires too early — Lighthouse captures the third-party cookies (CLID/SM/MUID)
 * in its trace window and downranks "Best Practices" score on every page (BP 77/100).
 *
 * Real users see Clarity load on first scroll/click/touch (any engagement) or after
 * 10s of viewing — analytics coverage is preserved for any engaged session. Lighthouse
 * captures ~10s of trace with no interaction → Clarity bootstrap fires at the boundary
 * or after, keeping the BP audit clean.
 *
 * Lives in layout.tsx replacing the prior inline `<Script id="microsoft-clarity">` tag.
 */

const FALLBACK_TIMEOUT_MS = 10_000;

export function ClarityLoader() {
  useEffect(() => {
    let loaded = false;

    const bootstrap = () => {
      if (loaded) return;
      loaded = true;
      ensureClarity();
    };

    const events: (keyof WindowEventMap)[] = [
      "mousedown",
      "touchstart",
      "scroll",
      "keydown",
    ];

    const cleanup = () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onInteract));
    };

    const onInteract = () => {
      bootstrap();
      cleanup();
    };

    events.forEach((e) =>
      window.addEventListener(e, onInteract, { passive: true, once: true }),
    );
    const timer = setTimeout(() => {
      bootstrap();
      cleanup();
    }, FALLBACK_TIMEOUT_MS);

    return cleanup;
  }, []);

  return null;
}
